import React from 'react';
import { Space } from 'antd';
import { get } from 'lodash';
import { useAtomValue } from 'jotai';
import MetricTrend from './MetricTrend';
import {
  ANRandCrashDonutSection,
  ANRandCrashDonutsWrapper,
  ANRandCrashDonutMetricSection,
  TrendWrapper,
} from './styled';
import { useWidget } from '../../state/dashboard';
import useFetchMetrics from './useFetchMetrics';
import useDiagDashboard from '../../hooks/useDiagDashboard';
import Drilldown from '../../control/Drilldown';
import RemoveWidget from '../../control/RemoveWidget';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import { Retry } from '@src/components/common';
import { unitAtom } from '@src/components/metrics-panel-hz/atoms';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import formatGaugeRingData from '@utils/overview/gaugeRing';
import BaseChart from '@src/components/common/BaseChart';
import { useSelector } from 'react-redux';
import { currentMetricsMetaSelector } from '@src/store/common/selectors';

const RED_COLOR = '#c32928';
const BLUE_COLOR = '#85c3ff';
const GRAY_COLOR = '#cccccc';

function ANRWidget({ wid }) {
  const unit = useAtomValue(unitAtom);
  const widgetHandler = useWidget({ wid });
  const metricMeta = useSelector(currentMetricsMetaSelector);
  const { timeseriesMetrics = [], donutChartMetrics = [], groupName } = widgetHandler.state;
  const metrics: any = [...timeseriesMetrics, ...donutChartMetrics];
  const { isFetching, isError, data, refetch, queued } = useFetchMetrics({ wid, metrics });

  // TODO: update "metrics" to include the -> restart and -> crash metrics as well
  const { createDashboard } = useDiagDashboard({ metrics, title: groupName });

  // Create chart instance array for each donut
  const chartInstancesRef = React.useRef<any[]>([]);

  const DONUT_COLORS = [
    [RED_COLOR, GRAY_COLOR], // second donut: red, gray
    [BLUE_COLOR, GRAY_COLOR], // first donut: blue, gray
  ];

  // Each donut shows metric value as colored portion, remainder as gray
  const donutChartOptions = React.useMemo(() => {
    const anrCount = get(data, ['totals', 'anr_count'], 0);

    return donutChartMetrics.map((metric, idx) => {
      const metricValue = get(data, ['totals', metric], 0);
      const remainder = Math.max(anrCount - metricValue, 0);
      const option =
        formatGaugeRingData(
          [
            { value: metricValue, name: metric },
            { value: remainder, name: 'Other' },
          ],
          DONUT_COLORS[idx],
        ) || {};
      return option;
    });
  }, [data, donutChartMetrics, DONUT_COLORS]);

  React.useEffect(() => {
    chartInstancesRef.current.forEach((instance, idx) => {
      if (instance) {
        instance.setOption(donutChartOptions[idx]);
      }
    });
  }, [donutChartOptions, chartInstancesRef, data]);

  return (
    <WidgetCardWrapper
      wid={wid}
      title={groupName}
      style={{ gridRow: 'span 14' }}
      extra={
        <Space>
          <Drilldown createDashboard={createDashboard} />
          <RemoveWidget onClick={widgetHandler.remove} />
        </Space>
      }
    >
      <Retry
        isLoading={isFetching}
        showError={isError}
        fetchData={refetch}
        progress={true}
        queued={queued}
      >
        <div style={{ position: 'relative', height: '50%' }}>
          <TrendWrapper>
            {timeseriesMetrics.map((metricKey) => {
              const percentageMetric = getPercentageMetricByKey({
                metricKey,
                defaultValue: metricKey,
              });
              const chartMetric = unit === '%' ? percentageMetric : metricKey;

              return (
                <MetricTrend
                  style={{}}
                  wid={wid}
                  key={chartMetric}
                  metricKey={chartMetric}
                  data={get(data, ['data', chartMetric], [])}
                  total={get(data, ['totals', chartMetric], null)}
                />
              );
            })}
          </TrendWrapper>
        </div>

        <ANRandCrashDonutsWrapper>
          {donutChartMetrics.map((metric, idx) => (
            <ANRandCrashDonutSection key={metric}>
              <BaseChart
                chartInstanceRefCb={(instance) => {
                  chartInstancesRef.current[idx] = instance;
                }}
                style={{ height: 80, width: 80 }}
              />
              <ANRandCrashDonutMetricSection>
                <p>{metricMeta[metric]?.displayName || ''}</p>
                <p style={{ color: DONUT_COLORS[idx][0] }}>{get(data, ['totals', metric], 0)}</p>
              </ANRandCrashDonutMetricSection>
            </ANRandCrashDonutSection>
          ))}
        </ANRandCrashDonutsWrapper>
      </Retry>
    </WidgetCardWrapper>
  );
}

export default ANRWidget;
